import { useEffect, useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import FMESidebar from './FMESidebar';
import FMEStatusBar from './FMEStatusBar';
import TutorialStep from '../Tutorial';
import { getCookie } from '../../services/Cookies';
import { ModelContextProvider, useModelContext } from './ModelContext';
import { ReadyState } from 'react-use-websocket';
import { useSetAgentModelInfo } from '../../hooks/useAgentContext';
import { useGlobalWebSocketHandler } from '../../shared/hooks/useGlobalWebSocketHandler';
import { useAppNavigation } from '../../hooks/useAppNavigation';
import SubPages from '../../pages/subpages/SubPagesEnum';

// Get current view from pathname
function getCurrentView(pathname: string): string {
    if (pathname.includes('/dashboard')) return 'dashboard';
    if (pathname.includes('/financial-statements')) return 'financial-statements';
    if (pathname.includes('/audit')) return 'audit';
    if (pathname.includes('/history')) return 'history';
    if (pathname.includes('/tools')) return 'tools';
    if (pathname.includes('/compare')) return 'compare';
    if (pathname.includes('/calculation-results')) return 'calculations';
    if (pathname.includes('/ai')) return 'AI';
    if (pathname.includes('/models/editor')) return 'architecture';
    return 'architecture';
}

export default function ModelLayout() {
    const location = useLocation();
    const currentView = getCurrentView(location.pathname);

    // Extraire l'entityId depuis l'URL (dernier segment numérique du chemin)
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const lastSegment = pathSegments[pathSegments.length - 1];
    const urlEntityId = /^\d+$/.test(lastSegment) ? parseInt(lastSegment, 10) : null;

    // Récupérer l'entité depuis le state de navigation (priorité maximale)
    const stateEntity = location.state?.entity ?? null;

    // Récupérer l'entité du cookie
    const rawCookie = getCookie("entity");
    const cookieEntity = rawCookie && rawCookie !== "" && rawCookie !== 'undefined'
        ? JSON.parse(rawCookie)
        : null;

    // Résolution de l'entité :
    // 1. State de navigation (toujours fiable, passé via navigateToPage)
    // 2. Cookie si son ID correspond à l'entityId de l'URL (même entité → même modèle)
    // 3. Résolution via API si l'URL a un entityId mais le cookie ne correspond pas
    // 4. Cookie par défaut si pas d'entityId dans l'URL
    let currentEntity: any;
    let fallbackEntityId: number | null = null;

    if (stateEntity) {
        currentEntity = stateEntity;
    } else if (cookieEntity && (!urlEntityId || cookieEntity.id === urlEntityId)) {
        currentEntity = cookieEntity;
    } else if (urlEntityId) {
        // L'entityId URL ne correspond pas au cookie → le cookie est pour un autre modèle
        // Résolution asynchrone via API dans ModelContextProvider
        currentEntity = null;
        fallbackEntityId = urlEntityId;
    } else {
        currentEntity = cookieEntity;
    }

    return (
        <ModelContextProvider initialEntity={currentEntity} initialEntityId={fallbackEntityId}>
            <ModelLayoutInner currentView={currentView} />
        </ModelContextProvider>
    );
}

function ModelLayoutInner({ currentView }: { currentView: string }) {
    const { model, currentScenario, currentSensitivity, currentEntity, statusBarProps, isComputing, isComputed, auditPassed, readyState, isEntityLoading, entityError, lastJsonMessage, contextFirstError, contextSensitivityErrorCount } = useModelContext();
    const setAgentModelInfo = useSetAgentModelInfo();
    const navigate = useNavigate();
    const navigateToPage = useAppNavigation();

    const handleNavigateToArchitecture = useCallback(() => {
        navigateToPage(SubPages.FinancialModelEditor, currentEntity);
    }, [navigateToPage, currentEntity]);

    // Global WebSocket cache handler — alive across all model pages
    useGlobalWebSocketHandler({
        lastJsonMessage,
        scenarioId: currentScenario?.id ?? 0,
        currentSensitivity,
        currentScenario,
    });

    // Sync model context up to the global AgentContextProvider
    useEffect(() => {
        if (model?.id) {
            setAgentModelInfo({
                model_id: model.id,
                model_name: model.name,
                scenario_id: currentScenario?.id,
                scenario_name: currentScenario?.name,
                sensitivity_id: currentSensitivity?.id,
                sensitivity_name: currentSensitivity?.name,
            });
        }
        return () => setAgentModelInfo(null);
    }, [model?.id, model?.name, currentScenario?.id, currentScenario?.name, currentSensitivity?.id, currentSensitivity?.name, setAgentModelInfo]);

    if (isEntityLoading) {
        return (
            <div className="h-full flex items-center justify-center bg-white">
                <div className="flex flex-col items-center gap-3">
                    <CircularProgress size={36} />
                    <p className="text-sm text-slate-500">Loading model...</p>
                </div>
            </div>
        );
    }

    if (entityError) {
        return (
            <div className="h-full flex items-center justify-center bg-white">
                <div className="flex flex-col items-center gap-3 text-center">
                    <p className="text-sm text-red-600">{entityError}</p>
                    <button
                        className="text-sm text-blue-600 hover:underline"
                        onClick={() => navigate('/app/models')}
                    >
                        Back to models
                    </button>
                </div>
            </div>
        );
    }

    // Compute state comes from ModelContext (always alive via WebSocket).
    // FME-specific extras (errors, write access) come from statusBarProps.
    // On non-architecture pages, use context-level errors (fetched from API)
    // and redirect clicks to the architecture page.
    const effectiveStatusBarProps = {
        ...statusBarProps,
        isComputing,
        isComputed: isComputed ?? currentScenario?.is_computed,
        auditPassed,
        isConnected: readyState === ReadyState.OPEN,
        // Use context-level errors as fallback until FME provides its own
        firstError: statusBarProps.firstError ?? contextFirstError,
        sensitivityErrorCount: statusBarProps.sensitivityErrorCount ?? contextSensitivityErrorCount,
        ...(currentView !== 'architecture' ? {
            onErrorClick: handleNavigateToArchitecture,
            onSensitivityErrorClick: handleNavigateToArchitecture,
        } : {}),
    };

    return (
        <div className="h-full flex flex-col bg-white">
            {/* Content Area (Sidebar + Main) */}
            <div className="flex flex-1 min-h-0 overflow-hidden">
                {/* Sidebar - Persistent across route changes */}
                <FMESidebar
                    currentView={currentView}
                    model={model}
                    currentScenario={currentScenario}
                    currentSensitivity={currentSensitivity}
                />

                {/* Main Content Area */}
                <div className="flex-1 flex flex-col min-h-0 min-w-0">
                    {/* Status Bar - shared across all model pages */}
                    <TutorialStep tutorial='main' identifier='status_bar' display='block' position="center" marginTop={10}>
                        <div className="px-6 py-2 border-b border-slate-100 bg-slate-50/80 flex items-center gap-4 shrink-0">
                            <FMEStatusBar {...effectiveStatusBarProps} />
                        </div>
                    </TutorialStep>
                    <div className="flex-1 min-h-0 overflow-hidden">
                        <Outlet />
                    </div>

                    {/* Tutorial final step - shown on any model page */}
                    <TutorialStep tutorial='main' identifier='enjoy' display='block' fixedCenter>
                        <div></div>
                    </TutorialStep>
                </div>
            </div>

        </div>
    );
}
